
import { useEffect, useState } from 'react';
import { MessageCircle, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { isDevelopment } from '@/utils/environment';

const TawkToWidget = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    // Only load after user interaction to avoid blocking LCP
    const handleUserInteraction = () => {
      if (!userInteracted) {
        setUserInteracted(true);
        loadTawkTo();
      }
    };

    // Listen for user interactions
    const events = ['mousedown', 'touchstart', 'keydown', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
    });

    // Fallback: Load after 3 seconds if no interaction
    const fallbackTimer = setTimeout(() => {
      if (!userInteracted) {
        setUserInteracted(true);
        loadTawkTo();
      }
    }, 3000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
      clearTimeout(fallbackTimer);
    };
  }, [userInteracted]);

  const loadTawkTo = () => {
    try {
      // Check if Tawk_API already exists
      if (window.Tawk_API) {
        setIsLoaded(true);
        return;
      }

      // Initialize Tawk_API
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();

      // Load script asynchronously
      const script = document.createElement("script");
      const firstScript = document.getElementsByTagName("script")[0];
      
      script.async = true;
      script.src = 'https://embed.tawk.to/66a53c1d32dca6db2cb69649/1i3qn5kmc';
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');

      // Handle successful load - improved detection
      script.onload = () => {
        console.log('Tawk.to script loaded successfully');
        
        // Wait a bit for the widget to initialize
        setTimeout(() => {
          // Check if the widget is actually available and working
          if (window.Tawk_API && typeof window.Tawk_API.onLoad === 'function') {
            window.Tawk_API.onLoad = function() {
              console.log('Tawk.to widget is ready and loaded');
              setIsLoaded(true);
              setHasError(false);
              setShowFallback(false);
            };
          } else {
            // If API exists but widget isn't fully loaded, mark as loaded anyway
            setIsLoaded(true);
            setHasError(false);
            setShowFallback(false);
          }
        }, 1000);
      };

      // Handle load errors
      script.onerror = (error) => {
        console.warn('Tawk.to widget failed to load:', error);
        setHasError(true);
        setShowFallback(true);
      };

      firstScript.parentNode?.insertBefore(script, firstScript);

      // Set timeout for loading (10 seconds instead of 5)
      const timeout = setTimeout(() => {
        if (!isLoaded && !window.Tawk_API) {
          console.warn('Tawk.to widget loading timeout - no API detected');
          setHasError(true);
          setShowFallback(true);
        }
      }, 10000);

      return () => {
        clearTimeout(timeout);
      };
    } catch (error) {
      console.warn('Error initializing Tawk.to widget:', error);
      setHasError(true);
      setShowFallback(true);
    }
  };

  // Compact fallback contact component
  const FallbackContact = () => (
    <div className="fixed bottom-4 right-4 z-50">
      <Card className="w-64 shadow-lg border border-nexx-green/20">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle className="h-4 w-4 text-nexx-green" />
            <span className="font-medium text-sm text-gray-900">Need Help?</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFallback(false)}
              className="ml-auto p-0 h-5 w-5 text-gray-500"
            >
              ×
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              asChild
              size="sm"
              className="flex-1 bg-nexx-green hover:bg-green-700 text-xs py-1"
            >
              <a href="tel:+12402198426" className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                Call
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="flex-1 border-nexx-green text-nexx-green hover:bg-nexx-green hover:text-white text-xs py-1"
            >
              <a href="mailto:support@nexx.net" className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Email
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <>
      {/* Only show fallback if there's actually an error AND TawkTo failed to load */}
      {showFallback && hasError && <FallbackContact />}
      {!isLoaded && !hasError && !isDevelopment() && userInteracted && (
        <div className="fixed bottom-4 right-4 z-40">
          <div className="bg-nexx-green text-white px-2 py-1 rounded text-xs">
            Loading chat...
          </div>
        </div>
      )}
    </>
  );
};

// Extend window type for Tawk_API
declare global {
  interface Window {
    Tawk_API?: any;
    Tawk_LoadStart?: Date;
  }
}

export default TawkToWidget;
